import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Rss, RefreshCw, Plus, Trash2, ExternalLink, AlertTriangle, CheckCircle2, X,
  Search, Filter, ShieldCheck, Sparkles,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import {
  adminCreateSource,
  adminUpdateSource,
  adminDeleteSource,
  adminRefreshSource,
  adminRefreshAllSources,
  adminGetSourcesRegistry,
  adminGetSourcesRegistryOrphans,
} from "@/services/adminService";

const formatRelative = (iso) => {
  if (!iso) return "لم يُحدَّث بعد";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `قبل ${mins} د`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `قبل ${hrs} س`;
  const days = Math.round(hrs / 24);
  return `قبل ${days} يوماً`;
};

const HEALTH_META = {
  healthy:    { label: "سليم",        cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  warning:    { label: "تحذير",       cls: "border-amber-200 bg-amber-50 text-amber-700" },
  failed:     { label: "فشل",         cls: "border-red-200 bg-red-50 text-red-700" },
  configured: { label: "مُهيَّأ — غير مُفعَّل", cls: "border-fursa-200 bg-fursa-50 text-fursa-700" },
  configured_not_connected: { label: "مُهيَّأ — غير متصل", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  unknown:    { label: "—",           cls: "border-surface-200 bg-surface-50 text-ink-500" },
};

const HealthPill = ({ health }) => {
  const meta = HEALTH_META[health] || HEALTH_META.unknown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const StatusPill = ({ status }) => {
  if (status === "ok") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-fursa-200 bg-fursa-50 px-2 py-0.5 text-[10px] font-semibold text-fursa-700">
        <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
        ok
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">
        <AlertTriangle className="h-3 w-3" strokeWidth={2} />
        خطأ
      </span>
    );
  }
  return null;
};

const TrustBar = ({ score }) => {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const tone = v >= 85 ? "bg-fursa-700" : v >= 70 ? "bg-fursa-500" : v >= 55 ? "bg-gold-500" : "bg-surface-300";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-100">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
      <span dir="ltr" className="font-mono text-xs font-bold tabular-nums text-ink-700">{v}</span>
    </div>
  );
};

const AddSourceForm = ({ onSubmit, isSaving, onCancel }) => {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [region, setRegion] = useState("");
  const [category, setCategory] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) {
      toast.error("العنوان والرابط مطلوبان");
      return;
    }
    const ok = await onSubmit({ name: name.trim(), url: url.trim(), region: region.trim() || null, category: category.trim() || null });
    if (ok) {
      setName(""); setUrl(""); setRegion(""); setCategory("");
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-source-form"
      className="rounded-xl border border-surface-200 bg-white p-5 shadow-card"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">اسم المصدر</span>
          <input
            data-testid="admin-source-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="مثلاً: العربية · اقتصاد"
            className="h-10 rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">رابط RSS</span>
          <input
            data-testid="admin-source-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            dir="ltr"
            placeholder="https://example.com/rss.xml"
            className="h-10 rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">المنطقة (اختياري)</span>
          <input
            data-testid="admin-source-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="مثلاً: الخليج / شمال أفريقيا"
            className="h-10 rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">التصنيف (اختياري)</span>
          <input
            data-testid="admin-source-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="مثلاً: حكومي / إعلامي"
            className="h-10 rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
          />
        </label>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-surface-100 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center rounded-lg border border-surface-200 bg-white px-4 text-sm font-medium text-ink-700 transition-colors hover:bg-surface-50"
        >
          إلغاء
        </button>
        <button
          type="submit"
          disabled={isSaving}
          data-testid="admin-source-submit"
          className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-fursa-700 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Plus className="h-4 w-4" strokeWidth={1.75} />
          إضافة المصدر
        </button>
      </div>
    </form>
  );
};

// ─── Filter dropdown (native <select> — matches existing minimal aesthetic) ──
const SelectFilter = ({ label, value, onChange, options, testId }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-9 min-w-36 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none transition-colors hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

export default function SourcesPage() {
  const [sources, setSources] = useState([]);            // DB-backed rows
  const [orphans, setOrphans] = useState([]);            // overrides w/o DB row
  const [lookups, setLookups] = useState({ countries: [], categories: [], sectors: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [category, setCategory] = useState("all");
  const [health, setHealth] = useState("all");
  const [active, setActive] = useState("all");           // all / active / inactive / orphan
  const [sortBy, setSortBy] = useState("priority");      // priority | trust | last_fetch

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [reg, orph] = await Promise.all([
        adminGetSourcesRegistry(),
        adminGetSourcesRegistryOrphans(),
      ]);
      setSources(Array.isArray(reg?.sources) ? reg.sources : []);
      setOrphans(Array.isArray(orph?.orphans) ? orph.orphans : []);
      setLookups({
        countries:  Array.isArray(reg?.countries)  ? reg.countries  : [],
        categories: Array.isArray(reg?.categories) ? reg.categories : [],
        sectors:    Array.isArray(reg?.sectors)    ? reg.sectors    : [],
      });
    } catch (e) {
      toast.error("تعذّر تحميل قائمة المصادر", { description: String(e) });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSource = async (payload) => {
    setPendingId("__new__");
    try {
      await adminCreateSource(payload);
      toast.success("تمت إضافة المصدر بنجاح");
      setShowForm(false);
      await load();
      return true;
    } catch (e) {
      const msg = String(e);
      if (msg.includes(" 409 ")) toast.error("هذا الرابط مسجَّل مسبقاً");
      else toast.error("تعذّر إضافة المصدر", { description: msg });
      return false;
    } finally {
      setPendingId(null);
    }
  };

  const toggleActive = async (id, next) => {
    setPendingId(id);
    try {
      await adminUpdateSource(id, { is_active: next });
      setSources((prev) => prev.map((s) => (s.id === id ? { ...s, is_active: next } : s)));
      toast.success(next ? "تم تفعيل المصدر" : "تم إيقاف المصدر مؤقتاً");
    } catch (e) {
      toast.error("تعذّر تحديث المصدر", { description: String(e) });
    } finally {
      setPendingId(null);
    }
  };

  const removeSource = async (id, name) => {
    if (!window.confirm(`حذف مصدر "${name}"؟ سيتم إزالة كل بنوده أيضاً.`)) return;
    setPendingId(id);
    try {
      await adminDeleteSource(id);
      setSources((prev) => prev.filter((s) => s.id !== id));
      toast.success("تم حذف المصدر");
    } catch (e) {
      toast.error("تعذّر الحذف", { description: String(e) });
    } finally {
      setPendingId(null);
    }
  };

  const refreshOne = async (id) => {
    setPendingId(id);
    try {
      const res = await adminRefreshSource(id);
      if (res.status === "ok") {
        toast.success(`تم التحديث · ${res.new} بند جديد / ${res.skipped} مكرَّر`);
      } else {
        toast.error("فشل التحديث", { description: res.error_message || "خطأ غير معروف" });
      }
      await load();
    } catch (e) {
      toast.error("تعذّر التحديث", { description: String(e) });
    } finally {
      setPendingId(null);
    }
  };

  const refreshAll = async () => {
    setPendingId("__all__");
    try {
      const res = await adminRefreshAllSources();
      const nNew = res.results.reduce((a, r) => a + (r.new || 0), 0);
      const nErr = res.results.filter((r) => r.status === "error").length;
      toast.success(`تحديث كامل · ${nNew} بند جديد${nErr ? ` · ${nErr} خطأ` : ""}`);
      await load();
    } catch (e) {
      toast.error("تعذّر التحديث الشامل", { description: String(e) });
    } finally {
      setPendingId(null);
    }
  };

  // ─── Merge, filter, sort ────────────────────────────────────
  const allRows = useMemo(() => {
    const db = sources.map((s) => ({ ...s, _kind: "db" }));
    const or = orphans.map((s) => ({ ...s, _kind: "orphan" }));
    return [...db, ...or];
  }, [sources, orphans]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = allRows;
    if (q) {
      rows = rows.filter((r) => {
        const hay = [r.name, r.display_name, r.url, r.hostname, r.category, r.category_label]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    if (country !== "all") {
      rows = rows.filter((r) => (r.country_from_override || r.region) === country);
    }
    if (category !== "all") {
      rows = rows.filter((r) => (r.category_label || r.category) === category);
    }
    if (health !== "all") {
      rows = rows.filter((r) => r.health === health);
    }
    if (active === "active")   rows = rows.filter((r) => r._kind === "db" &&  r.is_active);
    if (active === "inactive") rows = rows.filter((r) => r._kind === "db" && !r.is_active);
    if (active === "orphan")   rows = rows.filter((r) => r._kind === "orphan");

    const sorted = [...rows].sort((a, b) => {
      if (sortBy === "trust")   return (Number(b.trust_score) || 0) - (Number(a.trust_score) || 0);
      if (sortBy === "last_fetch") {
        const at = a.last_fetched_at ? new Date(a.last_fetched_at).getTime() : 0;
        const bt = b.last_fetched_at ? new Date(b.last_fetched_at).getTime() : 0;
        return bt - at;
      }
      // default: priority (1 highest) → trust desc as tiebreaker
      const pa = Number(a.priority) || 99;
      const pb = Number(b.priority) || 99;
      if (pa !== pb) return pa - pb;
      return (Number(b.trust_score) || 0) - (Number(a.trust_score) || 0);
    });
    return sorted;
  }, [allRows, search, country, category, health, active, sortBy]);

  const activeCount = sources.filter((s) => s.is_active).length;

  const countryOptions = useMemo(() => ([
    { value: "all", label: "كل الدول" },
    ...(lookups.countries || []).map((c) => ({ value: c, label: c })),
  ]), [lookups.countries]);

  const categoryOptions = useMemo(() => ([
    { value: "all", label: "كل التصنيفات" },
    ...(lookups.categories || []).map((c) => ({ value: c, label: c })),
  ]), [lookups.categories]);

  return (
    <AdminLayout>
      <section className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
              Feed Sources · Intelligence Registry
            </p>
            <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
              مصادر التغذية الإخبارية
            </h2>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
              سجلّ ذكاء المصادر — درجات الثقة، الأولوية، التصنيف، والحالة الصحية لكل مصدر.
              المصادر المُهيَّأة في سياسة المصادر ولكنها غير مُفعَّلة تظهر بعلامة{" "}
              <span className="font-semibold text-fursa-700">مُهيَّأ — غير مُفعَّل</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              disabled={pendingId === "__all__" || sources.length === 0}
              data-testid="admin-sources-refresh-all"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-semibold text-ink-700 shadow-sm transition-colors hover:border-fursa-300 hover:text-fursa-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${pendingId === "__all__" ? "animate-spin" : ""}`} strokeWidth={1.75} />
              تحديث الجميع
            </button>
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              data-testid="admin-sources-toggle-form"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800"
            >
              {showForm ? <X className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />}
              {showForm ? "إلغاء" : "مصدر جديد"}
            </button>
          </div>
        </header>

        {showForm && (
          <AddSourceForm
            onSubmit={addSource}
            isSaving={pendingId === "__new__"}
            onCancel={() => setShowForm(false)}
          />
        )}

        {/* KPI strip — compact on mobile, expanded on desktop */}
        <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
          {[
            { label: "الإجمالي",              short: "الإجمالي", value: sources.length,                          key: "total" },
            { label: "نشِطة",                 short: "نشط",      value: activeCount,                             key: "active" },
            { label: "معطَّلة",                short: "موقف",     value: sources.length - activeCount,            key: "off" },
            { label: "بأخطاء آخر تحديث",      short: "خطأ",      value: sources.filter((s) => s.last_fetch_status === "error").length, key: "errors" },
            { label: "مُهيَّأ — غير مُفعَّل",   short: "مُهيَّأ",   value: orphans.length,                          key: "orphan" },
          ].map((c) => (
            <div
              key={c.key}
              data-testid={`admin-sources-kpi-${c.key}`}
              className="rounded-lg border border-surface-200 bg-white px-2 py-2 shadow-card sm:rounded-xl sm:p-4"
            >
              <p className="truncate text-[9px] font-semibold uppercase tracking-wider text-ink-500 sm:text-[10px] sm:tracking-widest">
                <span className="sm:hidden">{c.short}</span>
                <span className="hidden sm:inline">{c.label}</span>
              </p>
              <p dir="ltr" className="mt-1 font-mono text-base font-bold tabular-nums text-fursa-700 sm:mt-2 sm:text-2xl">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar: search + filters + sort */}
        <div className="rounded-xl border border-surface-200 bg-white p-4 shadow-card">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-64 flex-1 flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">بحث</span>
              <div className="relative">
                <Search className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" strokeWidth={1.75} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="اسم، نطاق، تصنيف…"
                  data-testid="admin-sources-search"
                  className="h-9 w-full rounded-lg border border-surface-200 bg-white ps-3 pe-9 text-sm outline-none placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
                />
              </div>
            </label>

            <SelectFilter
              label="الدولة"
              value={country}
              onChange={setCountry}
              options={countryOptions}
              testId="admin-sources-filter-country"
            />
            <SelectFilter
              label="التصنيف"
              value={category}
              onChange={setCategory}
              options={categoryOptions}
              testId="admin-sources-filter-category"
            />
            <SelectFilter
              label="الحالة الصحية"
              value={health}
              onChange={setHealth}
              options={[
                { value: "all",        label: "كل الحالات" },
                { value: "healthy",    label: "سليم" },
                { value: "warning",    label: "تحذير" },
                { value: "failed",     label: "فشل" },
                { value: "configured", label: "مُهيَّأ" },
              ]}
              testId="admin-sources-filter-health"
            />
            <SelectFilter
              label="التفعيل"
              value={active}
              onChange={setActive}
              options={[
                { value: "all",      label: "الكل" },
                { value: "active",   label: "نشِطة" },
                { value: "inactive", label: "معطَّلة" },
                { value: "orphan",   label: "مُهيَّأ — غير مُفعَّل" },
              ]}
              testId="admin-sources-filter-active"
            />
            <SelectFilter
              label="الترتيب حسب"
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: "priority",   label: "الأولوية" },
                { value: "trust",      label: "درجة الثقة" },
                { value: "last_fetch", label: "آخر تحديث" },
              ]}
              testId="admin-sources-sort"
            />
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-surface-100 pt-3 text-[11px] text-ink-500">
            <span className="inline-flex items-center gap-1.5"><Filter className="h-3 w-3" strokeWidth={1.75} /> {filtered.length} نتيجة</span>
            <span className="inline-flex items-center gap-1.5"><HealthPill health="healthy" /> نشط ضمن نافذة الفحص</span>
            <span className="inline-flex items-center gap-1.5"><HealthPill health="warning" /> تجاوز نافذة الفحص</span>
            <span className="inline-flex items-center gap-1.5"><HealthPill health="failed" /> آخر جلب فشل</span>
            <span className="inline-flex items-center gap-1.5"><HealthPill health="configured" /> مُعرَّف في السياسة</span>
          </div>
        </div>

        {/* Sources list */}
        {isLoading && allRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center text-sm text-ink-500">
            جاري تحميل المصادر…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-fursa-50 text-fursa-700">
              <Rss className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-ink-900">لا نتائج مطابقة</h3>
            <p className="mt-2 text-sm text-ink-500">
              جرّب تعديل الفلاتر أو حذفها.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card">
            <table className="w-full text-right" data-testid="admin-sources-table">
              <thead className="border-b border-surface-200 bg-surface-50 text-[10px] uppercase tracking-widest text-ink-500">
                <tr>
                  <th className="px-5 py-3.5 text-start font-semibold">المصدر</th>
                  <th className="px-5 py-3.5 font-semibold">الثقة</th>
                  <th className="px-5 py-3.5 font-semibold">الدولة / القطاع</th>
                  <th className="px-5 py-3.5 font-semibold">التصنيف</th>
                  <th className="px-5 py-3.5 font-semibold">آخر تحديث</th>
                  <th className="px-5 py-3.5 font-semibold">الحالة</th>
                  <th className="px-5 py-3.5 text-center font-semibold">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100 text-sm">
                {filtered.map((s) => {
                  const isOrphan = s._kind === "orphan";
                  const rowId = s.id || `orphan-${s.hostname}`;
                  const busy = pendingId === s.id;
                  const displayName = s.display_name || s.name || s.hostname;
                  const country = s.country_from_override || s.region;
                  return (
                    <tr
                      key={rowId}
                      data-testid={`admin-source-row-${rowId}`}
                      data-orphan={isOrphan ? "true" : "false"}
                      className={`group transition-colors hover:bg-fursa-50/40 ${busy ? "opacity-60" : ""} ${!isOrphan && !s.is_active ? "text-ink-500" : ""} ${isOrphan ? "bg-fursa-50/30" : ""}`}
                    >
                      <td className="max-w-md px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-ink-900">{displayName}</span>
                          {s.authoritative && (
                            <span
                              title="مصدر موثوق (Authoritative)"
                              className="inline-flex items-center gap-1 rounded-full border border-gold-200 bg-gold-50 px-1.5 py-0.5 text-[10px] font-semibold text-gold-700"
                              data-testid={`admin-source-auth-${rowId}`}
                            >
                              <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                              موثوق
                            </span>
                          )}
                          {isOrphan && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-fursa-200 bg-fursa-50 px-1.5 py-0.5 text-[10px] font-semibold text-fursa-700"
                              data-testid={`admin-source-orphan-${rowId}`}
                            >
                              <Sparkles className="h-3 w-3" strokeWidth={2} />
                              مُهيَّأ — غير مُفعَّل
                            </span>
                          )}
                        </div>
                        <a
                          dir="ltr"
                          href={s.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-500 hover:text-fursa-700"
                        >
                          <span className="truncate max-w-md">{s.hostname || s.url}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                        </a>
                      </td>
                      <td className="px-5 py-4">
                        <TrustBar score={s.trust_score} />
                        <div className="mt-1 text-[10px] text-ink-500">
                          أولوية <span dir="ltr" className="font-mono font-semibold text-ink-700">{s.priority ?? "—"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs text-ink-700">
                        <div className="font-medium">{country || <span className="text-ink-500">—</span>}</div>
                        <div className="text-ink-500">{s.sector || "—"}</div>
                      </td>
                      <td className="px-5 py-4 text-xs text-ink-700">
                        <div>{s.category_label || s.category || <span className="text-ink-500">—</span>}</div>
                        {s.source_type && (
                          <div className="mt-0.5 inline-flex items-center rounded border border-surface-200 bg-surface-50 px-1.5 py-0.5 text-[10px] font-mono uppercase text-ink-500">
                            {s.source_type}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs text-ink-500">
                        {isOrphan ? (
                          <span className="text-ink-500">—</span>
                        ) : (
                          <>
                            {formatRelative(s.last_fetched_at)}
                            {s.last_error && (
                              <div className="mt-1 max-w-xs truncate text-[11px] text-red-700" title={s.last_error}>
                                {s.last_error}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-2">
                          <HealthPill health={s.health} />
                          {!isOrphan && <StatusPill status={s.last_fetch_status} />}
                          {!isOrphan && (
                            <button
                              type="button"
                              onClick={() => toggleActive(s.id, !s.is_active)}
                              disabled={busy}
                              data-testid={`admin-source-toggle-${s.id}`}
                              aria-label={s.is_active ? "إيقاف مؤقّت" : "تفعيل"}
                              className={`inline-flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${s.is_active ? "bg-fursa-600" : "bg-surface-200"}`}
                            >
                              <span className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${s.is_active ? "-translate-x-4" : "translate-x-0"}`} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {isOrphan ? (
                          <div className="text-center text-[11px] text-ink-500">
                            أضِف كمصدر
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={() => refreshOne(s.id)}
                              disabled={busy}
                              data-testid={`admin-source-refresh-${s.id}`}
                              title="تحديث"
                              aria-label="تحديث المصدر"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fursa-700 transition-colors hover:bg-fursa-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} strokeWidth={1.75} />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeSource(s.id, displayName)}
                              disabled={busy}
                              data-testid={`admin-source-delete-${s.id}`}
                              title="حذف"
                              aria-label="حذف المصدر"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}
